/**
 * Area Types Page (Admin Only)
 * Manage area/overlay types used in projects
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

import AppBar from '../../elements/AppBar';
import PageBody from '../../elements/PageBody';
import PageTitle from '../../elements/PageTitle';
import { colorPalette } from '../../../theme';
import * as api from '../../../services/api';

const AreaTypesPage = () => {
  // State
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [areaToDelete, setAreaToDelete] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#4A90A4',
    sequence: 0,
    icon: '',
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Fetch areas
  const fetchAreas = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.listAreas();
      setAreas(response.items || []);
    } catch (err) {
      console.error('Error fetching areas:', err);
      setError(err.message || 'Failed to load area types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAreas();
  }, [fetchAreas]);

  // Open modal for add
  const handleOpenAddModal = () => {
    setEditingArea(null);
    setFormData({
      name: '',
      description: '',
      color: '#4A90A4',
      sequence: areas.length,
      icon: '',
    });
    setFormError(null);
    setModalOpen(true);
  };

  // Open modal for edit
  const handleOpenEditModal = (area) => {
    setEditingArea(area);
    setFormData({
      name: area.name || '',
      description: area.description || '',
      color: area.color || '#4A90A4',
      sequence: area.sequence || 0,
      icon: area.icon || '',
    });
    setFormError(null);
    setModalOpen(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingArea(null);
    setFormError(null);
  };

  // Handle form change
  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  // Submit form
  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setFormError('Name is required');
      return;
    }
    
    setFormLoading(true);
    setFormError(null);
    
    try {
      if (editingArea) {
        await api.updateArea(editingArea.id, formData);
        setSnackbar({ open: true, message: 'Area type updated successfully', severity: 'success' });
      } else {
        await api.createArea(formData);
        setSnackbar({ open: true, message: 'Area type created successfully', severity: 'success' });
      }
      
      handleCloseModal();
      fetchAreas();
    } catch (err) {
      console.error('Error saving area:', err);
      setFormError(err.message || 'Failed to save area type');
    } finally {
      setFormLoading(false);
    }
  };

  // Open delete confirmation
  const handleOpenDeleteDialog = (area) => {
    setAreaToDelete(area);
    setDeleteDialogOpen(true);
  };

  // Delete area
  const handleDelete = async () => {
    if (!areaToDelete) return;
    
    setFormLoading(true);
    
    try {
      await api.deleteArea(areaToDelete.id);
      setSnackbar({ open: true, message: 'Area type deleted successfully', severity: 'success' });
      setDeleteDialogOpen(false);
      setAreaToDelete(null);
      fetchAreas();
    } catch (err) {
      console.error('Error deleting area:', err);
      setSnackbar({ open: true, message: err.message || 'Failed to delete area type', severity: 'error' });
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: colorPalette.backgroundColor }}>
      <AppBar />
      
      <PageBody>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <PageTitle>
            Area Types Management
          </PageTitle>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={fetchAreas} disabled={loading} title="Refresh">
              <RefreshIcon />
            </IconButton>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAddModal}
              sx={{ backgroundColor: colorPalette.asseTracOrange }}
            >
              Add Area Type
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#302F2E' }}>
                <TableCell sx={{ color: 'white', fontWeight: 600, width: 60 }}>#</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Description</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Color</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : areas.length > 0 ? (
                areas.map((area) => (
                  <TableRow key={area.id} hover>
                    <TableCell>{area.sequence || 0}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {area.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {area.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={area.color || '#4A90A4'}
                        sx={{
                          backgroundColor: area.color || '#4A90A4',
                          color: 'white',
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        title="Edit"
                        onClick={() => handleOpenEditModal(area)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        title="Delete"
                        color="error"
                        onClick={() => handleOpenDeleteDialog(area)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No area types found. Click "Add Area Type" to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </PageBody>

      {/* Add/Edit Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingArea ? 'Edit Area Type' : 'Add Area Type'}
        </DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Name"
                fullWidth
                required
                value={formData.name}
                onChange={handleChange('name')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={formData.description}
                onChange={handleChange('description')}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Color"
                type="color"
                fullWidth
                value={formData.color}
                onChange={handleChange('color')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Sequence"
                type="number"
                fullWidth
                value={formData.sequence}
                onChange={handleChange('sequence')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Icon (optional)"
                fullWidth
                value={formData.icon}
                onChange={handleChange('icon')}
                placeholder="e.g., water, construction, wetland"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={formLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={formLoading}
            sx={{ backgroundColor: colorPalette.asseTracOrange }}
          >
            {formLoading ? <CircularProgress size={20} /> : (editingArea ? 'Save Changes' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Area Type</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{areaToDelete?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={formLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={formLoading}
          >
            {formLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AreaTypesPage;
